import { useSetState } from '@/shared/lib/customHooks';
import { useEffect } from 'react';
import type { HowToVideosProps, HowToVideosState } from './howToVideos.types';

/**
 * Backs the how-to-videos support tab with its per-device sub-tab state. Defaults to sub-tab '1', derives the shown video from either videoBiofluxSelected or videoBiotresSelected depending on the active tab, and preselects the first Bioflux and Biotres entry whenever the howtoVideos prop arrives.
 *
 * @param howtoVideos - Videos grouped under bioflux and biotres device keys.
 * @returns Sub-tab state with its setter, the selected video and the tab navigator.
 */
export const useHowToVideos = ({ howtoVideos }: Pick<HowToVideosProps, 'howtoVideos'>) => {
  const [state, setState] = useSetState<HowToVideosState>({
    activeTab: '1',
    videoBiofluxSelected: undefined,
    videoBiotresSelected: undefined,
  });

  /**
   * The how-to video currently selected for the active device sub-tab. Reads either the Bioflux or the Biotres selection out of state depending on whether the active sub-tab is '1'.
   *
   * @returns The selected video entry, or undefined when none is chosen.
   */
  const videoSelect =
    state[state.activeTab === '1' ? 'videoBiofluxSelected' : 'videoBiotresSelected'];

  /**
   * Switches the how-to-videos view to another device sub-tab. Writes the tab into state only when it differs from the currently active one.
   *
   * @param tab - Key of the device sub-tab to activate.
   * @returns Nothing.
   */
  const navigateTab = (tab: any) => {
    if (tab !== state.activeTab) {
      setState({ activeTab: tab });
    }
  };

  useEffect(() => {
    if (howtoVideos) {
      setState({
        videoBiofluxSelected: howtoVideos.bioflux?.[0] || undefined,
        videoBiotresSelected: howtoVideos.biotres?.[0] || undefined,
      });
    }
  }, [howtoVideos]);

  return {
    state,
    setState,
    videoSelect,
    navigateTab,
  };
};
